/**
 * The client-side document tree model behind the file-tree navigation drawer
 * (DocTreeNav / DocTreePanel). Built from the flat `documents` index the app
 * already fetches — no backend involvement. The tree shows the LITERAL file
 * structure (every real path segment becomes a folder), matching the Documents
 * index persona: what you see here is what `ls` would show.
 */

import { fileStem, journalDayFromPath } from './docView';

/** The slice of a `documents` row the tree needs. */
export interface DocTreeDoc {
  path: string;
  type: string | null;
  treeLabel: string;
  spaceId: string | null;
}

export interface DocTreeFolder {
  kind: 'folder';
  /** Stable expansion key: `<treeLabel>/<segments…>`. */
  id: string;
  /** The literal path segment. */
  name: string;
  children: DocTreeNode[];
}

export interface DocTreeLeaf {
  kind: 'doc';
  id: string;
  /** The filename stem, exactly as on disk. */
  name: string;
  /** The FULL document path (space prefix included) — the `/docs/…` link target. */
  path: string;
  type: string | null;
}

export type DocTreeNode = DocTreeFolder | DocTreeLeaf;

/** One tree per provenance: Personal, then each shared space. */
export interface DocTreeRoot {
  label: string;
  children: DocTreeNode[];
}

/** Path within its own tree: space docs drop the leading `<slug>/` prefix. */
function relativePath(doc: DocTreeDoc): string {
  if (doc.spaceId) {
    const idx = doc.path.indexOf('/');
    return idx >= 0 ? doc.path.slice(idx + 1) : doc.path;
  }
  return doc.path;
}

/**
 * Recursive child sort: folders before documents; folders by name; journal day
 * documents newest-first and everything else by stem — the same reading order
 * as the Documents index rows.
 */
function sortChildren(children: DocTreeNode[]): void {
  children.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
    if (a.kind === 'folder') return a.name.localeCompare(b.name);
    const dayA = journalDayFromPath(a.path);
    const dayB = journalDayFromPath((b as DocTreeLeaf).path);
    if (dayA && dayB) return dayB.localeCompare(dayA);
    return a.name.localeCompare(b.name);
  });
  for (const child of children) {
    if (child.kind === 'folder') sortChildren(child.children);
  }
}

/**
 * Builds the display tree from the flat document list: one root per tree label
 * (Personal first, spaces alphabetically), literal nested folders beneath.
 */
export function buildDocTree(docs: DocTreeDoc[]): DocTreeRoot[] {
  const roots = new Map<string, DocTreeNode[]>();

  for (const doc of docs) {
    if (!roots.has(doc.treeLabel)) roots.set(doc.treeLabel, []);
    let children = roots.get(doc.treeLabel)!;

    const segments = relativePath(doc).split('/').filter(Boolean);
    let idPrefix = doc.treeLabel;
    for (let i = 0; i < segments.length - 1; i += 1) {
      idPrefix = `${idPrefix}/${segments[i]}`;
      let folder = children.find(
        (node): node is DocTreeFolder => node.kind === 'folder' && node.name === segments[i]
      );
      if (!folder) {
        folder = { kind: 'folder', id: idPrefix, name: segments[i], children: [] };
        children.push(folder);
      }
      children = folder.children;
    }

    children.push({
      kind: 'doc',
      id: `${idPrefix}/${segments[segments.length - 1] ?? doc.path}`,
      name: fileStem(doc.path),
      path: doc.path,
      type: doc.type,
    });
  }

  const result: DocTreeRoot[] = [...roots.entries()].map(([label, children]) => {
    sortChildren(children);
    return { label, children };
  });
  result.sort((a, b) => {
    if (a.label === 'Personal') return -1;
    if (b.label === 'Personal') return 1;
    return a.label.localeCompare(b.label);
  });
  return result;
}

function findAncestors(nodes: DocTreeNode[], docPath: string, trail: string[]): string[] | null {
  for (const node of nodes) {
    if (node.kind === 'doc') {
      if (node.path === docPath) return trail;
    } else {
      const found = findAncestors(node.children, docPath, [...trail, node.id]);
      if (found) return found;
    }
  }
  return null;
}

/**
 * The folder ids containing the given document path, outermost first — what
 * the tree expands to reveal the currently open document. Empty when the path
 * isn't in the tree (root-level documents included: nothing to expand).
 */
export function ancestorIdsForPath(roots: DocTreeRoot[], docPath: string): string[] {
  for (const root of roots) {
    const found = findAncestors(root.children, docPath, []);
    if (found) return found;
  }
  return [];
}
